import { t } from '@lingui/macro'
import { useEffect, useRef } from 'react'
import styled, { useTheme } from 'styled-components'

import { FlexColumn, FlexRow } from '@/@bothrs-ui/web/components/layout/flex/Flex'
import { P } from '@/@bothrs-ui/web/components/typography/Body'
import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { breakpoints } from '@/config/layout'
import { mixpanel } from '@/mixpanel'
import { InfiniteScrollList } from '@/modules/shared/components/infinite-scroll-list/InfiniteScrollList'
import { Loader } from '@/modules/shared/components/loader/Loader'
import { useLoggedInForMandatee } from '@/modules/trust/hooks/useLoggedInForMandatee'
import { MessageCenterTab } from '../../pages/MessageRoot.page'
import { MessageItem } from '../message-item/MessageItem'

import type { Message } from '../../types/message.types'

type MessageListProps = {
  /**
   * The id of the message that is currently selected
   */
  selectedMessageId?: string

  /**
   * The list of messages to display
   */
  messages: Message[]

  /**
   * Whether there are more messages to load
   */
  hasMore: boolean

  /**
   * Whether the list is loading
   */
  isLoading: boolean

  /**
   * The active tab (pending or resolved)
   */
  activeTab: MessageCenterTab

  /**
   * The callback to call when the list is scrolled to the bottom
   */
  onEndReached: () => void

  /**
   * The callback to call when a message is selected
   */
  onMessageSelected: (messageId: string) => void
}

export const MessageList = ({
  selectedMessageId,
  messages,
  hasMore,
  isLoading,
  activeTab,
  onMessageSelected,
  onEndReached,
}: MessageListProps) => {
  // Refs
  // ------------------------------------------------------------------------- /
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Hooks
  // ------------------------------------------------------------------------- /
  const theme = useTheme()

  // Methods
  // ------------------------------------------------------------------------- /
  const keyExtractor = (message: Message) => message.id

  // Render
  // ------------------------------------------------------------------------- /
  const renderMessageItem = (message: Message) => {
    return (
      <StListItem
        key={message.id}
        id={`message_${message.id}`}
        onClick={() => onMessageSelected(message.id)}
        data-active={selectedMessageId && message?.id ? message?.id === selectedMessageId : undefined}
        data-read-status={message.readStatus}
      >
        <MessageItem message={message} />
      </StListItem>
    )
  }

  return (
    <StyledScrollableListContainer ref={scrollContainerRef}>
      <InfiniteScrollList<Message>
        items={messages}
        renderItem={renderMessageItem}
        keyExtractor={keyExtractor}
        onEndReached={onEndReached}
        hasMore={hasMore}
        scrollableRootRef={scrollContainerRef}
        showDividers={false}
        ListFooterComponent={
          isLoading && messages.length > 0 ? (
            <div style={{ height: '50px' }}>
              <Loader small />
            </div>
          ) : null
        }
        ListEmptyComponent={
          isLoading ? (
            <Loader />
          ) : (
            <EmptyListIndicator>
              {activeTab === MessageCenterTab.Unread ? (
                <>
                  <TrackView event='message_empty_state_unread' />
                  <EmptyListImage
                    src='/img/empty-pending-message-center.png'
                    alt={t({
                      id: 'message.empty-open-messages',
                      message: 'Je hebt geen ongelezen berichten',
                    })}
                  />
                </>
              ) : (
                <>
                  <TrackView event='message_empty_state_read' />
                  <EmptyListImage
                    src='/img/empty-pending-message-center.png'
                    alt={t({
                      id: 'message.empty-resolved-messages',
                      message: 'Je hebt nog geen gelezen berichten',
                    })}
                  />
                </>
              )}
              <FlexColumn gap={theme.UI.SpacingPx.Space2} alignItems='center'>
                <H3 noMargin>
                  {activeTab === MessageCenterTab.Unread
                    ? t({
                        id: 'message.empty-pending-messages',
                        message: 'Je hebt geen ongelezen berichten',
                      })
                    : t({
                        id: 'message.empty-read-messages',
                        message: 'Je hebt nog geen gelezen berichten',
                      })}
                </H3>
                <P
                  type='BodySmallRegular'
                  style={{ padding: `0 ${theme.UI.SpacingPx.Space3}` }}
                  color={theme.Colors.Neutrals.Darker}
                >
                  {activeTab === MessageCenterTab.Unread
                    ? t({
                        id: 'message.empty-open-messages-description',
                        message: 'Je ontvangt hier een overzicht van je berichten die je van ons ontvangt.',
                      })
                    : t({
                        id: 'message.empty-resolved-messages-description',
                        message: 'Hier volgt later een overzicht van al je gelezen berichten.',
                      })}
                </P>
              </FlexColumn>
            </EmptyListIndicator>
          )
        }
      />
    </StyledScrollableListContainer>
  )
}

const StyledScrollableListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  height: 100%;

  padding-top: ${({ theme }) => theme.UI.SpacingPx.Space4};

  @media ${breakpoints.fromL} {
    padding-left: ${({ theme }) => theme.UI.SpacingPx.Space12};
  }
`

const EmptyListIndicator = styled(FlexColumn)`
  gap: ${({ theme }) => theme.UI.SpacingPx.Space4};
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.UI.SpacingPx.Space8};

  text-align: center;
  height: 100%;
`

const EmptyListImage = styled.img`
  width: 100%;
  max-width: 120px;
  height: auto;
  object-fit: contain;

  color: ${({ theme }) => theme.Colors.Neutrals.Darker};
`

const StListItem = styled(FlexRow)<{ active?: boolean }>`
  width: 100%;
  cursor: pointer;
  padding: ${({ theme }) => theme.UI.SpacingPx.Space4};
  background-color: ${({ theme }) => theme.Colors.Background.White};
  position: relative;
  transition: background 0.2s ease-in-out;
  margin-bottom: ${({ theme }) => theme.UI.SpacingPx.Space1};

  padding: ${({ theme }) => theme.UI.SpacingPx.Space6}
    ${({ theme }) => theme.UI.SpacingPx.Space6};

  @media ${breakpoints.fromM} {
    padding: ${({ theme }) => theme.UI.SpacingPx.Space6}
      ${({ theme }) => theme.UI.SpacingPx.Space8};
  }

  @media ${breakpoints.fromL} {
    border-radius: 12px 0 0 12px;
    background: ${({ theme }) => theme.Components.Card.Fill};
    transition: background 0.2s ease-in-out;
  }

  &[data-active='true'] {
    background: ${({ theme }) => theme.Colors.Neutrals.Lighter};
  }
  &:hover {
    background: ${({ theme }) => theme.Colors.Neutrals.Lighter};
  }
`

function TrackView({ event }: { event: string }) {
  const isLoggedInForMandatee = useLoggedInForMandatee()

  useEffect(() => {
    mixpanel.track(event, { origin, is_mandate: isLoggedInForMandatee })
  }, [event])

  return null
}
